import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiClientError, apiFetch } from './client';
import { companionsKeys } from './companions';
import { bagKeys } from './bag';

/** Where a skill trains today (server `TrainsAtDto`): a land map pin or a crafting bench. */
export type TrainsAtDto = {
  activityCode: string;
  activityName: string;
  landCode: string;
  kind: 'gather' | 'process' | string;
};

/**
 * One skill row of `GET /skills` (server `SkillLevelDto`). The three multipliers are the server's
 * own `SkillMath` figures for `level` — the client never re-derives the curve (spec §3.4).
 */
export type SkillLevelDto = {
  code: string;
  name: string;
  kind: string;
  level: number;
  xp: number;
  xpToNext: number;
  yieldMult: number;
  speedMult: number;
  preserveChance: number;
  trainsAt: TrainsAtDto;
};

/**
 * One `skill_sessions` row (server `SkillSessionDto`). Everything after `actionName` is resolved
 * from the catalog on every read so the busy card has nothing to derive (spec §3.5): `busyLabel`
 * is "<busyVerb> <item>" for a gather ("Chopping Copperwood Log") and the recipe name for a
 * process; `outputItem`/`outputQty` the node's item and base yield or the recipe's output;
 * `placeCode`/`placeName` the pin or bench.
 */
export type SkillSessionDto = {
  id: number;
  companionId: number;
  companionName: string;
  skillCode: string;
  actionCode: string;
  kind: string;
  startedAt: string;
  completesAt: string;
  notify: boolean;
  /** Content-store display name for the running action (item name on gather, recipe name on process). */
  actionName?: string;
  busyLabel: string;
  outputItem: string;
  outputQty: number;
  placeCode: string;
  placeName: string;
  /** Reward engine E1: the queue this session belongs to (1 = no queue) and how many sessions the
   *  sweep will still arm after it. Optional only because older fixtures predate V37; the server
   *  always sends both. */
  repeatTotal?: number;
  repeatsLeft?: number;
};

/**
 * What a completed session produced (server `SkillResultDto`, read off
 * `skill_sessions.result_json`) — the completion toast's input (spec §3.6). `sideFinds` is the
 * side-roll's item codes only.
 */
export type SkillResultDto = {
  sessionId: number;
  kind: string;
  outputItem: string;
  outputName: string;
  qty: number;
  xpGained: number;
  levelBefore: number;
  levelAfter: number;
  doubled: boolean;
  preserved: boolean;
  sideFinds: string[];
  /** Reward engine E1: why the queue ended early after this session; null when it did not. */
  queueStopped?: 'inputs' | 'unavailable' | 'busy' | null;
};

/** `GET /skills?companionId=`. `lastCompleted` is null until a session has ever completed. */
export type CompanionSkillsResponse = {
  skills: SkillLevelDto[];
  sessions: SkillSessionDto[];
  lastCompleted: SkillResultDto | null;
};

export type GatherNodeDto = {
  code: string;
  skill: string;
  activity: string;
  item: string;
  itemName?: string;
  skillName?: string;
  levelReq: number;
  durationMinutes: number;
  yield: number;
  xp: number;
};

export type RecipeIngredientDto = { item: string; qty: number; itemName?: string };
export type RecipeDto = {
  code: string;
  skill: string;
  skillName?: string;
  bench: string;
  name: string;
  levelReq: number;
  durationMinutes: number;
  xp: number;
  inputs: RecipeIngredientDto[];
  output: { item: string; qty: number; itemName?: string };
};

export const skillKeys = {
  all: ['skills'] as const,
  companion: (id: number) => [...skillKeys.all, 'companion', id] as const,
  sessions: () => [...skillKeys.all, 'sessions'] as const,
  nodes: (activity: string) => [...skillKeys.all, 'nodes', activity] as const,
  recipes: (activity: string) => [...skillKeys.all, 'recipes', activity] as const,
};

// Polling (spec §3.6): 10 s while the server says a session is running, off otherwise. The busy
// state is `sessions.length > 0`, never `completesAt > now` — the server owns "done", so a
// session past its deadline but not yet applied by the 15 s sweep keeps the poll alive until the
// row actually disappears. Function form, same shape as `useBattle`'s passthrough, so the
// interval follows the data with no extra state.
export function useCompanionSkills(companionId: number | undefined) {
  return useQuery({
    queryKey: skillKeys.companion(companionId ?? 0),
    queryFn: () => apiFetch<CompanionSkillsResponse>(`/skills?companionId=${companionId}`),
    enabled: companionId != null,
    refetchInterval: (q) => (q.state.data?.sessions.length ? 10_000 : false),
  });
}

/** The stable empty answer for `useActiveSkillSessions().data?.sessions ?? NO_SESSIONS` — one
 *  module-level array so an idle account hands its consumers the same identity on every render
 *  (a fresh `[]` per render would defeat every memo keyed on it). `readonly` so nobody can push
 *  into the shared instance. */
export const NO_SESSIONS: readonly SkillSessionDto[] = [];

/** Every active session on the account, all companions — the Habitat/World "who is busy where" read. */
export function useActiveSkillSessions() {
  return useQuery({
    queryKey: skillKeys.sessions(),
    queryFn: () => apiFetch<{ sessions: SkillSessionDto[] }>('/skills/sessions'),
    refetchInterval: (q) => (q.state.data?.sessions.length ? 15_000 : false),
  });
}

export function useGatherNodes(activity: string | undefined) {
  return useQuery({
    queryKey: skillKeys.nodes(activity ?? ''),
    queryFn: () => apiFetch<{ nodes: GatherNodeDto[] }>(`/skills/nodes?activity=${activity}`),
    enabled: Boolean(activity),
  });
}

export function useRecipes(activity: string | undefined) {
  return useQuery({
    queryKey: skillKeys.recipes(activity ?? ''),
    queryFn: () => apiFetch<{ recipes: RecipeDto[] }>(`/skills/recipes?activity=${activity}`),
    enabled: Boolean(activity),
  });
}

export function useStartSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { companionId: number; actionCode: string; notify: boolean; repeat?: number }) =>
      apiFetch<SkillSessionDto>('/skills', {
        method: 'POST',
        body: JSON.stringify({ ...input, idempotencyKey: crypto.randomUUID() }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
      queryClient.invalidateQueries({ queryKey: bagKeys.all });
      queryClient.invalidateQueries({ queryKey: companionsKeys.all });
    },
  });
}

export function useCancelSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) =>
      apiFetch(`/skills/${sessionId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
      queryClient.invalidateQueries({ queryKey: bagKeys.all });
    },
  });
}

/**
 * Reward engine E1: `POST /skills/{id}/stop` ends a queue after the running session (which still
 * completes; nothing is refunded). A Stop that reaches the server while the sweep is applying that
 * session gets a 404, and the sweep has by then armed the queue's next session — so on a 404 the
 * sessions are re-read and the re-armed one (same companion and action, still something left) is
 * stopped instead. `onSuccess` returns the invalidation so the mutation stays pending until the
 * card's data says `repeatsLeft = 0`: the line never reads "Last one" before the server does.
 */
export function useStopSkillQueue() {
  const queryClient = useQueryClient();
  const stop = (id: number) => apiFetch(`/skills/${id}/stop`, { method: 'POST' });
  return useMutation({
    mutationFn: async (session: SkillSessionDto) => {
      try {
        await stop(session.id);
      } catch (e) {
        if (!(e instanceof ApiClientError) || e.status !== 404) throw e;
        const { sessions } = await apiFetch<{ sessions: SkillSessionDto[] }>('/skills/sessions');
        const next = sessions.find((n) => n.companionId === session.companionId
          && n.actionCode === session.actionCode && (n.repeatsLeft ?? 0) > 0);
        if (next) await stop(next.id);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: skillKeys.all }),
  });
}
